import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import { toast } from "react-toastify";

import ActionButton from "../formInputs/buttons/ActionButton";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao.jsx";
import GradingAccordions from "../display/GradingAccordions.jsx";
import GroupAccordionItem from "../display/groupAccordion/GroupAccordionItem";
import isGradingProfileWindowOpen from "../../utils/ActionControls/isGradingProfileWindowOpen.js";
import LoadingSvg from "../loadingSvg/LoadingSvg.jsx";
import TabButton from "../../components/formInputs/buttons/TabButton.jsx";

import { byGradeThenCreator } from "../../utils/teacher/EprSummaryUtils.js";
import { formatDate } from "../../utils/DateFormatter.jsx";
import { GlobalContext } from "../contextProvider/ContextProvider.jsx";
import { gradeTranslation, indexToGrade } from "../../utils/SchoolAdmin/translations.jsx";
import { useEnsureSchoolTemplatesForLocation } from "../contextProvider/ContextSetter.jsx";

import "../../styles/EprSummary.scss";
import "../../styles/TeacherHome.scss";

/**
 * EprSummary component provides a summary view of existing grading profiles (EPRs)
 * and allows users to create new ones for available grades.
 *
 * @component
 * @param {Object} props
 * @param {Function} dispatch
 * @param {Object} state
 * @param {Array} releasedGrades
 * @param {Object} [teacherGradeIfReleased]
 * @returns {JSX.Element}
 */
const EprSummary = ({
    dispatch,
    releasedGrades,
    state,
    teacherGradeIfReleased,
    teacherGuid,
    schoolLevelTemplateDto: overrideSchoolLevelTemplateDto, // Rename prop to avoid conflict with context
    basePath,
    subjectUser
}) => {
    const { gradingProfiles, loader: profileLoader } = state;

    const { state: globalState, dispatch: globalDispatch } = useContext(GlobalContext);
    const ensureTemplatesForLocation = useEnsureSchoolTemplatesForLocation();

    const {
        actionWindowsDto,
        allLocationDtos,
        currentTimePeriodDto,
        schoolLevelTemplateDto: globalSchoolLevelTemplateDto,
        schoolTemplatesByLocation,
        schoolYearDto,
        timePeriodDto,
        token,
        userDetails,
        viewTimePeriodDto
    } = globalState || {};

    // Use override if provided, else global
    const schoolLevelTemplateDto = overrideSchoolLevelTemplateDto || globalSchoolLevelTemplateDto;

    const isWindowOpen =
        actionWindowsDto && currentTimePeriodDto?.key
            ? isGradingProfileWindowOpen(actionWindowsDto, currentTimePeriodDto.key)
            : false;

    const fetchedSharedGroupsByPeriodRef = useRef(new Set());

    const first = timePeriodDto?.find((tp) => tp.name === "1st Semester");
    const second = timePeriodDto?.find((tp) => tp.name === "2nd Semester");

    const isCurrentFirst = currentTimePeriodDto?.name === "1st Semester";

    const disableSecondTab = isCurrentFirst;

    const selectedTimePeriodDto = viewTimePeriodDto || currentTimePeriodDto || first;

    const isViewingCurrentSemester = selectedTimePeriodDto?.key === currentTimePeriodDto?.key;

    const selectedTimePeriodKey = Number(selectedTimePeriodDto?.key);

    const gradingProfilesForView = (gradingProfiles || []).filter(
        (p) => Number(p.timePeriod) === selectedTimePeriodKey
    );

    const [loader, setLoader] = useState(false);
    const [selectedGradeToCreate, setSelectedGradeToCreate] = useState(
        teacherGradeIfReleased?.gradeLevel || (releasedGrades?.length > 0 ? releasedGrades[0].gradeLevel : "")
    );
    const [savedMessage, setSavedMessage] = useState("");
    const [showCreateInitial, setShowCreateInitial] = useState(false);
    const [showAddAnother, setShowAddAnother] = useState(false);
    const [showSaved, setShowSaved] = useState(false);
    const [sharedGroups, setSharedGroups] = useState([]);

    const navigate = useNavigate();

    /**
     * Computes the list of grades available for creating an additional EPR
     * (excludes grades that already have profiles)
     */
    const availableGradesForAdditional = useMemo(() => {
        const filtered = releasedGrades.filter(
            (grade) => !gradingProfilesForView.some((prof) => String(prof.grade) === String(grade.profileGradeId))
        );
        return filtered;
    }, [releasedGrades, gradingProfilesForView]);

    /**
     * Handles selection change for the grade dropdown.
     * @param {Object} e
     */
    const handleSelectChange = (e) => {
        setSelectedGradeToCreate(e.target.value);
    };

    const handleCreate = () => {
        if (!isViewingCurrentSemester) return;
        const parentProfileId =
            currentTimePeriodDto?.name === "1st Semester"
                ? schoolLevelTemplateDto.firstSemester.profileId
                : schoolLevelTemplateDto.secondSemester.profileId;

        let gradeToUse = selectedGradeToCreate;
        if (!gradeToUse && releasedGrades?.length > 0) {
            gradeToUse = releasedGrades[0].gradeLevel;
        }

        const selectedGradeObj = releasedGrades.find((grade) => grade.gradeLevel === gradeToUse);
        if (!selectedGradeObj) {
            console.error("Failed to find grade object for:", gradeToUse, "in", releasedGrades);
            toast.error("Invalid grade selected. Please refresh and try again.");
            setLoader(false);
            return;
        }

        const options = {
            action: "createGradingProfile",
            data: {
                parentProfile: parentProfileId,
                grade: selectedGradeObj.profileGradeId,
                schoolYearKey: schoolYearDto.key,
                ownerGuid: teacherGuid || userDetails.uid,
                lastUpdaterGuid: userDetails.uid
            },
            token
        };
        setLoader(true);
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const payload = response.data.payload;
                    const createdProfile = payload.gradingProfileDto || payload;
                    dispatch({ type: "SET_FETCH_FLAG", payload: true });
                    dispatch({ type: "SET_SELECTED_EPR", payload: createdProfile });
                    toast.success(`Report Card successfully created for ${currentTimePeriodDto?.name}`, {
                        autoClose: 3000
                    });
                    navigate(`${basePath || "/home"}/summary/roster/${response.data.payload.key}`);
                    dispatch({ type: "SET_SELECTED_LINK", payload: "HOME/SUMMARY/ROSTER" });
                } else {
                    toast.error(
                        `Report Card could not be created for ${currentTimePeriodDto?.name}. Please try again later.`,
                        {
                            autoClose: 3000
                        }
                    );
                }
            })
            .catch((error) => {
                console.error("Error creating Report Card:", error);
                toast.error(
                    `Report Card could not be created for ${currentTimePeriodDto?.name}. Please try again later.`,
                    {
                        autoClose: false
                    }
                );
            })
            .finally(() => {
                setLoader(false);
            });
    };

    useEffect(() => {
        if (!sharedGroups || sharedGroups.length === 0) return;

        const uniqueLocationKeys = [...new Set(sharedGroups.map((g) => g.sharedLocationKey).filter(Boolean))];

        uniqueLocationKeys.forEach((locationKey) => {
            ensureTemplatesForLocation({ locationKey });
        });
    }, [sharedGroups, ensureTemplatesForLocation]);

    /*
     * Auto select initial grade when none is selected
     */
    useEffect(() => {
        if (selectedGradeToCreate || releasedGrades.length === 0) {
            return;
        }

        const gradeToSet = teacherGradeIfReleased?.gradeLevel || releasedGrades[0].gradeLevel;
        setSelectedGradeToCreate(gradeToSet);
    }, [releasedGrades, teacherGradeIfReleased, selectedGradeToCreate]);

    /*
     * Reset selection if current grade is no longer available
     */
    useEffect(() => {
        if (gradingProfilesForView.length > 0 && releasedGrades.length > 0) {
            const existingGrades = new Set(gradingProfilesForView.map((profile) => profile.grade));
            const firstMissingGrade = releasedGrades.find((grade) => !existingGrades.has(grade.profileGradeId));
            if (firstMissingGrade && !selectedGradeToCreate) {
                setSelectedGradeToCreate(firstMissingGrade.gradeLevel);
            }
        }
    }, [gradingProfilesForView, releasedGrades, selectedGradeToCreate]);

    useEffect(() => {
        if (!selectedGradeToCreate || availableGradesForAdditional.length === 0) return;

        const isStillAvailable = availableGradesForAdditional.some(
            (grade) => grade.gradeLevel === selectedGradeToCreate
        );

        if (!isStillAvailable) {
            setSelectedGradeToCreate(availableGradesForAdditional[0].gradeLevel);
        }
    }, [availableGradesForAdditional, selectedGradeToCreate]);

    const fetchAllSharedGroups = useCallback(() => {
        const options = {
            action: "getAllSharedGroups",
            params: {
                teacherGuid: teacherGuid || userDetails.uid,
                schoolYearKey: schoolYearDto.key,
                timePeriod: selectedTimePeriodDto.key
            },
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const sharedGroups = response.data.payload;
                    const manipulatedGroups = sharedGroups.map((group) => ({
                        value: `shared-${group.sharedGroupId}`,
                        label: `${group.sharedGroupCreatorDisplayName} - ${group.sharedGroupName.replace(/^INTERNAL_/, "").replace("_", " ")}`,
                        sharedGradingProfile: group.sharedGradingProfile,
                        groupType: "SHARED",
                        sharedGradeName: group.sharedGradeName,
                        groupTimePeriod: selectedTimePeriodDto?.key,
                        sharedLocationKey: group.locationKey,
                        sharedMeta: group
                    }));

                    setSharedGroups(manipulatedGroups);
                }
            })
            .catch((error) => {
                console.error("Error fetching shared groups:", error);
                toast.error("There was a problem loading shared groups information. Please try again later.");
            });
    }, [schoolYearDto, selectedTimePeriodDto, token, userDetails, teacherGuid]);

    useEffect(() => {
        if (!userDetails || !token || !schoolYearDto || !selectedTimePeriodDto?.key) return;

        if (!isViewingCurrentSemester) return;

        if (fetchedSharedGroupsByPeriodRef.current.has(selectedTimePeriodDto.key)) return;
        fetchedSharedGroupsByPeriodRef.current.add(selectedTimePeriodDto.key);

        fetchAllSharedGroups();
    }, [fetchAllSharedGroups, isViewingCurrentSemester, schoolYearDto, selectedTimePeriodDto?.key, token, userDetails]);

    useEffect(() => {
        const userToCheck = subjectUser || userDetails;
        if (userToCheck) {
            const hasMatchingInstructionalArea =
                Array.isArray(userToCheck.instructionalAreas) &&
                userToCheck.instructionalAreas?.some((ia) =>
                    ["51", "52", "53", "54", "55", "56", "57"].includes(ia.key)
                );

            if (hasMatchingInstructionalArea) {
                setShowCreateInitial(true);
            } else {
                const iAkey = userToCheck.userAttributeDto?.userAttributeMap?.INSTRUCTIONAL_AREA_KEY;
                if (["51", "52", "53", "54", "55", "56", "57"].includes(iAkey)) {
                    setShowCreateInitial(true);
                }
            }
        }
    }, [currentTimePeriodDto, fetchAllSharedGroups, schoolYearDto, token, userDetails, subjectUser]);

    const schoolLevelForSelected =
        selectedTimePeriodDto?.name === "1st Semester"
            ? schoolLevelTemplateDto?.firstSemester
            : schoolLevelTemplateDto?.secondSemester;

    const gradeObjects = schoolLevelForSelected?.gradeToSubjects;
    const profileExistForCurrentSem = gradingProfilesForView.some((prof) =>
        gradeObjects?.find((grade) => String(grade.profileGradeId) === String(prof.grade))
    );
    const showIntitialSetup =
        isWindowOpen &&
        ((gradingProfilesForView.length === 0 && releasedGrades.length > 0) ||
            (releasedGrades.length > 0 && !profileExistForCurrentSem));

    const showNoGrades =
        !schoolLevelForSelected ||
        (gradeObjects && (!gradingProfiles || gradingProfilesForView.length === 0) && releasedGrades.length === 0);

    const sharedGroupsForView = [...sharedGroups].filter(
        (group) => Number(group.groupTimePeriod) === Number(selectedTimePeriodDto?.key)
    );

    const sharedGroupsBySchool = useMemo(() => {
        return sharedGroupsForView.reduce((acc, group) => {
            const key = group.sharedLocationKey ?? "unknown";
            acc[key] = acc[key] || [];
            acc[key].push(group);
            return acc;
        }, {});
    }, [sharedGroupsForView]);

    const gradeToRank = Object.keys(indexToGrade)
        .sort((a, b) => Number(a) - Number(b))
        .reduce((acc, k, i) => {
            acc[indexToGrade[k]] = i;
            return acc;
        }, {});

    const getSchoolName = (schoolKey) => {
        const match = allLocationDtos?.find((loc) => Number(loc.key) === Number(schoolKey));
        return match?.name ?? `School ${schoolKey}`;
    };

    if (profileLoader) {
        return <LoadingSvg />;
    }
    const profileWindow = actionWindowsDto?.find(
        (window) => window.differentiator === currentTimePeriodDto?.key && window.action === "GRADING_PROFILE"
    );

    const allowCreate =
        isWindowOpen &&
        gradingProfilesForView.length > 0 &&
        gradingProfilesForView.length < releasedGrades.length &&
        isViewingCurrentSemester;
    return (
        <div className="right-side-wrapper">
            <div className="right-page-title">
                <h4>
                    {gradingProfilesForView?.length === 0 && showCreateInitial
                        ? "Report Card Setup"
                        : "Report Card Summary"}
                </h4>
                <h5>
                    <span style={{ fontWeight: "700" }}>{currentTimePeriodDto?.name}</span> | {schoolYearDto.name}
                </h5>
            </div>
            {gradingProfiles.length > 0 && (
                <div style={{ display: "flex", gap: "8px", marginTop: "8px", marginBottom: "8px" }}>
                    <TabButton
                        className={
                            selectedTimePeriodDto?.name === "1st Semester" ? "tab-button-selected" : "tab-button"
                        }
                        onClick={() => {
                            globalDispatch({ type: "ViewTimePeriodDto", viewTimePeriodDto: first });
                        }}
                        size="small-no-width"
                    >
                        <div>
                            <p className="primary">1st Semester</p>
                        </div>
                    </TabButton>
                    <TabButton
                        className={
                            selectedTimePeriodDto?.name === "2nd Semester" ? "tab-button-selected" : "tab-button"
                        }
                        disable={disableSecondTab}
                        onClick={() => {
                            globalDispatch({ type: "ViewTimePeriodDto", viewTimePeriodDto: second });
                        }}
                        size="small-no-width"
                        width={135}
                    >
                        <div>
                            <p className="primary">2nd Semester</p>
                        </div>
                    </TabButton>
                </div>
            )}

            <hr />
            {!isWindowOpen && !gradingProfilesForView.length && showCreateInitial && (
                <div style={{ marginBottom: "12px" }}>
                    The Report Card window is currently closed for 2nd semester. Please check back on{" "}
                    {formatDate(profileWindow?.startDate, false)} to begin Report Card Setup.
                </div>
            )}
            {showNoGrades && (
                <div style={{ marginBottom: "12px" }}>No grades have been released yet. Please check again later.</div>
            )}
            {!showCreateInitial && !sharedGroups.length && (
                <div>
                    <div style={{ marginBottom: "12px" }}>
                        Specials/Elective Teachers, each homeroom teacher will share their homeroom class with you to
                        allow you to input ratings for the subject you teach.
                    </div>{" "}
                    Currently, no homeroom teacher has shared a class with you.
                </div>
            )}
            {showIntitialSetup && showCreateInitial && isViewingCurrentSemester && (
                <>
                    <div className="setup-helper-text">
                        To begin, select a grade to create your homeroom Report Card.
                        <div style={{ marginTop: "4px" }}>
                            Next, you will be prompted to assign students to the homeroom Report Card based on your IC
                            homeroom section.
                        </div>
                        <div style={{ marginTop: "4px" }}>
                            Finally, you will have the opportunity to share your homeroom Report Card with other
                            contributing professionals at your school.
                        </div>
                    </div>
                    <div className="add-additional-epr" style={{ display: "flex", marginTop: "16px" }}>
                        <label className="inline-select-label">
                            <div className="select-title">Create new Report Card for grade:</div>
                            <select
                                className="grade-select"
                                value={selectedGradeToCreate}
                                onChange={handleSelectChange}
                            >
                                {releasedGrades.map((grade) => (
                                    <option key={grade.gradeLevel} value={grade.gradeLevel}>
                                        {gradeTranslation[grade.gradeLevel] === "K"
                                            ? "Kindergarten"
                                            : `${gradeTranslation[grade.gradeLevel]} grade`}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <ActionButton label="Create New Report Card" onClick={handleCreate} />
                    </div>
                </>
            )}
            {allowCreate && (
                <>
                    {showAddAnother ? (
                        <button
                            type="button"
                            onClick={() => setShowAddAnother(!showAddAnother)}
                            className="hide-add-additional-btn"
                        >
                            Hide Add Additional Report Card
                        </button>
                    ) : (
                        <div style={{ marginLeft: "-8px", marginBottom: "24px" }}>
                            <ActionButton
                                className="action-button-reg"
                                onClick={() => {
                                    setShowAddAnother(!showAddAnother);
                                }}
                                label="Create Homeroom Report Card"
                            />
                        </div>
                    )}

                    {showAddAnother && (
                        <div className="add-additional-epr">
                            <div className="add-epr-note">
                                Note: Please delete an incorrectly created homeroom Report Card before creating a new
                                one.
                            </div>
                            <div className="add-additional-epr-form">
                                <label className="inline-select-label">
                                    <div className="select-title">Create new Report Card for grade:</div>
                                    <select
                                        className="grade-select"
                                        name="grade-select"
                                        value={selectedGradeToCreate}
                                        onChange={handleSelectChange}
                                    >
                                        {availableGradesForAdditional.map((grade) => (
                                            <option key={grade.gradeLevel} value={grade.gradeLevel}>
                                                {gradeTranslation[grade.gradeLevel] === "K"
                                                    ? "Kindergarten"
                                                    : `${gradeTranslation[grade.gradeLevel]} grade`}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <ActionButton label="Create New Report Card" onClick={handleCreate} />
                            </div>
                        </div>
                    )}
                </>
            )}
            {gradingProfilesForView.length > 0 && (
                <GradingAccordions
                    dispatch={dispatch}
                    gradingProfiles={gradingProfilesForView}
                    isHistoricalView={!isViewingCurrentSemester}
                    setSavedMessage={setSavedMessage}
                    setShowSaved={setShowSaved}
                    state={state}
                    schoolLevelTemplateDto={schoolLevelTemplateDto}
                    basePath={basePath}
                />
            )}
            {isViewingCurrentSemester && sharedGroupsForView && sharedGroupsForView.length > 0 && (
                <div>
                    {Object.entries(sharedGroupsBySchool).map(([locationKey, groups]) => {
                        const templatesForLocation = schoolTemplatesByLocation?.[locationKey];

                        const schoolLevelForSharedSchool =
                            selectedTimePeriodDto?.name === "1st Semester"
                                ? templatesForLocation?.firstSemester
                                : templatesForLocation?.secondSemester;

                        // Group by grade within each school
                        const groupsByGrade = groups.reduce((acc, group) => {
                            const grade = group.sharedGradeName || "Unknown";
                            if (!acc[grade]) acc[grade] = [];
                            acc[grade].push(group);
                            return acc;
                        }, {});

                        const showSchoolName = Object.keys(sharedGroupsBySchool).length > 1;

                        return (
                            <div key={locationKey}>
                                <h5 className="shared-group-section-title">
                                    {showSchoolName
                                        ? `${getSchoolName(locationKey)} - Groups Shared With Me`
                                        : "Groups Shared With Me"}
                                </h5>
                                {Object.entries(groupsByGrade)
                                    .sort(([gradeA], [gradeB]) => {
                                        const rankA = gradeToRank[gradeA] ?? Number.POSITIVE_INFINITY;
                                        const rankB = gradeToRank[gradeB] ?? Number.POSITIVE_INFINITY;
                                        return rankA - rankB;
                                    })
                                    .map(([gradeName, gradeGroups]) => (
                                        <div key={gradeName}>
                                            <h6 className="shared-school-header">
                                                {gradeName === "K" ? "Kindergarten" : `${gradeName} Grade`}
                                            </h6>
                                            {gradeGroups
                                                .slice()
                                                .sort(byGradeThenCreator)
                                                .map((group) => {
                                                    const grade = schoolLevelForSharedSchool?.gradeToSubjects?.find(
                                                        (g) => gradeTranslation[g.gradeLevel] === group.sharedGradeName
                                                    );

                                                    return (
                                                        <Accordion key={group.value}>
                                                            <GroupAccordionItem
                                                                key={group.value}
                                                                dispatch={dispatch}
                                                                eventKey={String(group.value)}
                                                                grade={grade}
                                                                group={group}
                                                                schoolLevelForSelected={schoolLevelForSharedSchool}
                                                                basePath={basePath}
                                                                type="SHARED"
                                                            />
                                                        </Accordion>
                                                    );
                                                })}
                                        </div>
                                    ))}
                            </div>
                        );
                    })}
                </div>
            )}
            {loader && <LoadingSvg />}
            {showSaved && <div className="saved-status-bar">{savedMessage}</div>}
        </div>
    );
};

EprSummary.propTypes = {
    dispatch: PropTypes.func.isRequired,
    releasedGrades: PropTypes.array.isRequired,
    state: PropTypes.object.isRequired,
    teacherGradeIfReleased: PropTypes.object,
    teacherGuid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    schoolLevelTemplateDto: PropTypes.object,
    basePath: PropTypes.string,
    subjectUser: PropTypes.object
};

EprSummary.defaultProps = {
    teacherGradeIfReleased: null,
    teacherGuid: null,
    schoolLevelTemplateDto: null,
    basePath: "/home",
    subjectUser: null
};

export default EprSummary;
